/**
 * Process Steps block entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS,
 * so the theme ships both together.
 *
 * Three jobs: bring the section in when it is scrolled to, drive which step is
 * showing, and read the drag that asks for the next one.
 *
 * No slider library: this is four panels, two buttons, one gesture and no
 * autoplay, loop or pagination. Swiper would be the theme's first runtime
 * dependency and several times the weight of the section it drives, so the
 * behaviour is the lines below and the motion stays in CSS — which also means
 * the global prefers-reduced-motion guard covers it without a second check
 * here.
 *
 * render.php already marks the first step active and dims the controls, so the
 * section is correct before this runs and stays correct if it never does.
 */

import './index.scss';

const sections = document.querySelectorAll('.process-steps');

if (sections.length) {
    /*
     * The entrance waits for the section to arrive rather than playing to an
     * empty room. Older browsers without the observer keep the section in its
     * end state — degraded decoration, never missing content.
     */
    const revealer =
        'IntersectionObserver' in window
            ? new IntersectionObserver(
                  (entries) => {
                      for (const entry of entries) {
                          if (entry.isIntersecting) {
                              entry.target.setAttribute('data-reveal', 'in');
                              revealer.unobserve(entry.target);
                          }
                      }
                  },
                  { rootMargin: '0px 0px -15% 0px' }
              )
            : null;

    for (const section of sections) {
        if (revealer) {
            section.setAttribute('data-reveal', 'pending');
            revealer.observe(section);
        }

        const steps = [...section.querySelectorAll('.process-steps__step')];
        const previous = section.querySelector('.process-steps__arrow--prev');
        const next = section.querySelector('.process-steps__arrow--next');

        // One step is not a slider, and without both controls there is nothing
        // to drive it with — leave the markup exactly as it was rendered.
        if (steps.length < 2 || !previous || !next) {
            continue;
        }

        let current = Math.max(
            0,
            steps.findIndex((step) => 'true' === step.getAttribute('data-active'))
        );

        /**
         * Show one step and bring the controls in line with where it sits in
         * the run.
         *
         * @param {number} index Zero-based step to show.
         * @returns {void}
         */
        const show = (index) => {
            current = Math.min(steps.length - 1, Math.max(0, index));

            steps.forEach((step, i) => {
                if (i === current) {
                    step.setAttribute('data-active', 'true');
                } else {
                    step.removeAttribute('data-active');
                }
            });

            // aria-disabled, not disabled: a control that vanishes from the tab
            // order under the visitor's own finger throws focus back to the top
            // of the document.
            previous.setAttribute('aria-disabled', String(0 === current));
            next.setAttribute('aria-disabled', String(current === steps.length - 1));
        };

        /**
         * Step in a direction unless that end has already been reached.
         *
         * @param {HTMLElement} control Button that was activated.
         * @param {number}      delta   -1 for back, 1 for forward.
         * @returns {void}
         */
        const move = (control, delta) => {
            if ('true' === control.getAttribute('aria-disabled')) {
                return;
            }

            show(current + delta);
        };

        previous.addEventListener('click', () => move(previous, -1));
        next.addEventListener('click', () => move(next, 1));

        /*
         * A drag, which until now had nothing to reach this with. The steps are
         * stacked in one grid and crossfaded rather than laid out in a strip,
         * so there is no scrolling box for a browser to hand the gesture to —
         * it is read here and turned into the same step the arrows take.
         *
         * Pointer events rather than touch ones, so the same code answers a
         * finger, a pen and a mouse. A mouse is held to the photograph: it is
         * the one part of the panel with no text to select, and a drag across a
         * paragraph should still select that paragraph. A finger may start
         * anywhere, because there is no selection to protect and the whole
         * panel is what a thumb aims at.
         *
         * Measured at the end and never during: nothing is prevented while the
         * pointer is down, so a vertical drag scrolls the page as it always
         * did. The gesture counts only if it went further across than down,
         * which is what separates "next step" from "read on".
         */
        const stage = section.querySelector('.process-steps__stage') ?? section;
        const drag = { x: 0, y: 0, live: false };

        /**
         * Decide what one finished drag meant.
         *
         * @param {number} dx How far it went across.
         * @param {number} dy How far it went down.
         * @returns {void}
         */
        const settle = (dx, dy) => {
            if (!drag.live) {
                return;
            }

            drag.live = false;

            /* 40px is about a thumb's width: shorter than that is a tap or a
             * click that wandered, and this must not answer either. */
            if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) {
                return;
            }

            /* Dragged left means the next step arrives from the right, which is
             * the direction the content moves, not the hand. */
            move(dx < 0 ? next : previous, dx < 0 ? 1 : -1);
        };

        stage.addEventListener(
            'pointerdown',
            (event) => {
                /* Touch is answered by the pair of listeners below, which every
                 * browser fires. Taking it here as well would step twice for
                 * one swipe in the ones that raise both. */
                if ('touch' === event.pointerType) {
                    return;
                }

                drag.live =
                    event.isPrimary && Boolean(event.target.closest?.('.process-steps__media'));

                if (drag.live) {
                    drag.x = event.clientX;
                    drag.y = event.clientY;
                }
            },
            { passive: true }
        );

        /* And a finger, which not every browser reports as a pointer. */
        stage.addEventListener(
            'touchstart',
            (event) => {
                // One finger. Two is a pinch, and a pinch is not a swipe.
                drag.live = 1 === event.touches.length;

                if (drag.live) {
                    drag.x = event.touches[0].clientX;
                    drag.y = event.touches[0].clientY;
                }
            },
            { passive: true }
        );

        stage.addEventListener(
            'touchend',
            (event) => {
                const touch = event.changedTouches[0];

                if (touch) {
                    settle(touch.clientX - drag.x, touch.clientY - drag.y);
                }
            },
            { passive: true }
        );

        /* A photograph is draggable by default, and the browser taking the
         * gesture for a file drag means no pointerup ever arrives here. */
        stage.addEventListener('dragstart', (event) => {
            if (event.target.closest?.('.process-steps__media')) {
                event.preventDefault();
            }
        });

        /*
         * A pointer stream is cancelled the moment the browser claims the
         * gesture for itself — which for a finger it does almost at once, while
         * deciding whether this is a scroll. Answering that would drop the
         * swipe every time: the touch listeners are still following it, and
         * `touchend` still arrives with the whole distance. So only a mouse or
         * a pen gives up here.
         */
        stage.addEventListener('pointercancel', (event) => {
            if ('touch' !== event.pointerType) {
                drag.live = false;
            }
        });

        stage.addEventListener(
            'pointerup',
            (event) => {
                if ('touch' !== event.pointerType) {
                    settle(event.clientX - drag.x, event.clientY - drag.y);
                }
            },
            { passive: true }
        );

        // Arrow keys are the expected way through a carousel once any of its
        // controls has focus.
        section.addEventListener('keydown', (event) => {
            if ('ArrowLeft' === event.key) {
                move(previous, -1);
            } else if ('ArrowRight' === event.key) {
                move(next, 1);
            } else {
                return;
            }

            event.preventDefault();
        });

        show(current);
        section.setAttribute('data-slider', 'on');
    }
}
